import { useEffect } from 'react'
import { useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { UserX, Trash2, Inbox } from 'lucide-react'
import { api, type NotificationItem } from '@/lib/api'

/**
 * OPT-042：消息通知中心——2026-08-16 起仅记录流失预警（client_churn）。
 * 进入批量已读，支持逐条删除/清空。
 */
export function NotificationsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  // 进入页面即批量已读（导航徽标清零；幂等）
  useEffect(() => {
    api.markNotificationsRead()
      .then(() => qc.invalidateQueries({ queryKey: ['notifications', 'unread'] }))
      .catch(() => {})
  }, [qc])

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    queryFn: ({ pageParam }) => api.getNotifications(pageParam ? { cursor: pageParam } : {}),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
  const items = (data?.pages ?? []).flatMap((p) => p.items)

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] })
    qc.invalidateQueries({ queryKey: ['notifications', 'unread'] })
  }

  const del = useMutation({
    mutationFn: (id: number) => api.deleteNotification(id),
    onSuccess: invalidateAll,
  })
  const clear = useMutation({
    mutationFn: () => api.clearNotifications(),
    onSuccess: invalidateAll,
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">{t('notification.title')}</h1>
        <Card><CardContent className="p-6 text-center text-slate-400">{t('common.loading')}</CardContent></Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t('notification.title')}</h1>
        {items.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            data-testid="clear-all-notifications"
            onClick={() => {
              if (window.confirm(t('notification.confirmClear'))) clear.mutate()
            }}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> {t('notification.clearAll')}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          {items.length === 0 ? (
            <div className="py-12 text-center">
              <Inbox className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-400">{t('notification.empty')}</p>
            </div>
          ) : (
            <ul className="space-y-1" data-testid="notification-list">
              {items.map((n) => (
                <NotificationRow key={n.id} item={n} onDelete={() => del.mutate(n.id)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} data-testid="notification-load-more">
            {isFetchingNextPage ? t('common.loading') : t('common.loadMore')}
          </Button>
        </div>
      )}
    </div>
  )
}

function formatTime(ts: number, t: (k: string, options?: Record<string, unknown>) => string): string {
  const diff = Date.now() / 1000 - ts
  const mins = Math.floor(diff / 60)
  if (mins < 1) return t('notification.justNow')
  if (mins < 60) return t('notification.minutesAgo', { n: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('notification.hoursAgo', { n: hours })
  const days = Math.floor(hours / 24)
  if (days < 30) return t('notification.daysAgo', { n: days })
  return new Date(ts * 1000).toLocaleDateString()
}

function NotificationRow({ item, onDelete }: { item: NotificationItem; onDelete: () => void }) {
  const { t } = useTranslation()
  const p = item.payload ?? {}
  // 仅流失预警一种消息
  const text = t('notification.type.clientChurn', { clientName: p.clientName })
  return (
    <li
      className={`flex items-start gap-3 py-2.5 px-2 rounded-lg border-b border-slate-100 last:border-0 ${item.readAt ? '' : 'bg-blue-50/50'}`}
      data-testid="notification-item"
    >
      <span className="mt-0.5"><UserX className="h-4 w-4 text-orange-600" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 break-words">{text}</p>
        <p className="text-xs text-slate-400 mt-0.5">{formatTime(item.createdAt, t)}</p>
      </div>
      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-600" onClick={onDelete} aria-label={t('notification.delete')}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  )
}
